<template>
    <div class="vsb" :class="`vsb--${variant}`">
        <!-- Icon -->
        <div class="vsb__icon" :style="{ width: normalizeSize(60, true), height: normalizeSize(60, true) }">
            <!-- V3: star / sparkle -->
            <svg
                v-if="variant === 'v3'"
                width="30"
                height="30"
                viewBox="0 0 30 30"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M15 2.5L17.375 9.5L24.375 11.25L17.375 13.625L15 20L12.625 13.625L5.625 11.25L12.625 9.5L15 2.5Z"
                    fill="white"
                />
                <path
                    d="M23.75 17.5L24.75 20.375L27.5 21.25L24.75 22.25L23.75 25L22.75 22.25L20 21.25L22.75 20.375L23.75 17.5Z"
                    fill="#F3EAFA"
                />
            </svg>

            <!-- V2: pencil -->
            <svg v-else width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M6.25 18.7488L5 24.9988L11.25 23.7488L23.125 11.8738C23.6289 11.3801 23.9129 10.7043 23.9129 9.99882C23.9129 9.29334 23.6289 8.61755 23.125 8.12382L21.875 6.87382C21.3813 6.36991 20.7055 6.08594 20 6.08594C19.2945 6.08594 18.6187 6.36991 18.125 6.87382L6.25 18.7488Z"
                    fill="white"
                />
                <path d="M18.75 5.625L24.375 11.25" stroke="#7D2DBD" stroke-width="2" stroke-linecap="round" />
                <path
                    d="M21.375 6.875C21.375 7.84085 22.1591 8.625 23.125 8.625C24.0909 8.625 24.875 7.84085 24.875 6.875C24.875 5.90915 24.0909 5.125 23.125 5.125C22.1591 5.125 21.375 5.90915 21.375 6.875Z"
                    fill="#F3EAFA"
                />
            </svg>
        </div>

        <!-- Text content -->
        <div class="vsb__body">
            <p class="vsb__title poppinsBold" :style="{ fontSize: normalizeFontSize(21) }">
                {{ title }}
                <span class="vsb__badge poppinsBold ppv2-text-[#fff] ppv2-text-opacity-70">{{ badge }}</span>
            </p>
            <p class="vsb__desc ppv2-text-[#fff] ppv2-text-opacity-60" :style="{ fontSize: normalizeFontSize(14) }">
                {{ description }}
            </p>
        </div>

        <!-- CTA button -->
        <a :href="ctaHref" class="vsb__cta poppinsSemiBold" :class="variant === 'v3' ? 'vsb__cta--ghost' : 'vsb__cta--solid'">
            <svg
                v-if="variant === 'v3'"
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
            >
                <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            {{ ctaLabel }}
            <svg
                v-if="variant === 'v2'"
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
            >
                <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
        </a>
    </div>
</template>

<script>
import normalizeFontSize from '../../utils/normalizeFontSize'
import normalizeSize from '../../utils/normalizeSize'

export default {
    name: 'VersionSwitchBanner',
    props: {
        variant: {
            type: String,
            default: 'v3', // 'v3' = on new version, 'v2' = on old version
            validator: (v) => ['v2', 'v3'].includes(v),
        },
        v3Url: {
            type: String,
            default: '/back-office/v3/caisse',
        },
        v2Url: {
            type: String,
            default: '/back-office/caisse',
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
    },
    computed: {
        title() {
            return this.variant === 'v3'
                ? this.$t('checkout.version_banner.v3.title')
                : this.$t('checkout.version_banner.v2.title')
        },
        badge() {
            return this.variant === 'v3'
                ? this.$t('checkout.version_banner.v3.badge')
                : this.$t('checkout.version_banner.v2.badge')
        },
        description() {
            return this.variant === 'v3'
                ? this.$t('checkout.version_banner.v3.description')
                : this.$t('checkout.version_banner.v2.description')
        },
        ctaLabel() {
            return this.variant === 'v3' ? this.$t('checkout.version_banner.v3.cta') : this.$t('checkout.version_banner.v2.cta')
        },
        ctaHref() {
            return this.variant === 'v3' ? this.v2Url : this.v3Url
        },
    },
}
</script>

<style scoped lang="scss">
/* ── Design tokens ── */
$accent: #7c3aed;
$accent-light: #9d5cf6;
$border: rgba(255, 255, 255, 0.1);
$text: #fbfaff;
$text-muted: #c7c3e4;

.vsb {
    display: flex;
    align-items: center;
    gap: 20px;
    padding: 20px 24px;
    border-radius: 16px;
    border: 1px solid $border;
    background: linear-gradient(91.83deg, #1a0b3d 0%, #08122c 46%, #021427 100%);
    position: relative;
    overflow: hidden;
    width: 100%;
    box-sizing: border-box;
    margin-bottom: 20px;

    /* Radial glow — bottom-right corner */
    &::after {
        content: '';
        position: absolute;
        bottom: -100%;
        right: 17%;
        width: 320px;
        height: 320px;
        background: radial-gradient(circle, #ab6edc47 0%, transparent 70%);
        pointer-events: none;
    }
}

/* ── Icon pill ── */
.vsb__icon {
    flex-shrink: 0;
    border-radius: 14px;
    background: linear-gradient(135deg, #7d2dbd 0%, #ab6edc 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    z-index: 1;
}

/* ── Body ── */
.vsb__body {
    flex: 1 1 auto;
    min-width: 0;
    z-index: 1;
}

.vsb__title {
    margin: 0 0 4px;
    color: $text;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    line-height: 1.3;
}

.vsb__badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 10px;
    border-radius: 999px;
    background: #ab6edc38;
    border: 1px solid #ab6edc73;
    font-size: 11px;
    letter-spacing: 0.6px;
    text-transform: uppercase;
}

.vsb__desc {
    margin: 0;
    line-height: 1.55;
}

/* ── CTA Button ── */
.vsb__cta {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 11px 20px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
    text-decoration: none !important;
    transition:
        opacity 0.2s,
        background 0.2s,
        box-shadow 0.2s;
    z-index: 1;

    &:hover {
        opacity: 0.85;
        text-decoration: none !important;
    }

    /* Ghost style (V3 page → go back) */
    &--ghost {
        background: rgba(255, 255, 255, 0.08);
        border: 1px solid rgba(255, 255, 255, 0.2);
        color: $text !important;

        &:hover {
            background: rgba(255, 255, 255, 0.13);
            opacity: 1;
        }
    }

    /* Solid purple style (V2 page → go to V3) */
    &--solid {
        background: linear-gradient(99.95deg, #7d2dbd 0%, #9a44d6 100%);
        border: 1px solid transparent;
        color: #fff !important;
        box-shadow: 0px 8px 22px 0px #7d2dbd80;

        &:hover {
            box-shadow: 0px 8px 22px 0px #7d2dbd80;
            opacity: 1;
        }
    }
}

/* ── Responsive ── */
@media (max-width: 768px) {
    .vsb {
        flex-wrap: wrap;
        gap: 16px;
        padding: 16px;
    }
    .vsb__cta {
        width: 100%;
        justify-content: center;
    }
}
</style>
